<template>
  <div class="did">
    <shell @pageend="pageItems">
      <div class="container m-auto">
        <div class="w-full lg:w-1/2 m-auto">

          <note-select :path="path" :tags="tags" page="beans" :params="routeParams" type="Beans" />

          <div class="px-2">

            <div class="pb-4">
              <div
                v-if="prevPage"
                class="text-full-blue text-xs mb-4 underline cursor-pointer"
                @click="goBack(prevPage.route)">Back to <span class="font-bold">{{prevPage.name}}</span></div>
              <div class="text-lg text-black"><strong>{{displayDate}}</strong></div>
              
            </div>
            <bean-bar
              v-if="!year && !month"
              :items="allTime" />

            <bean-bar
              v-if="!year && !month"
              :items="yrs"
              heading="Yearly"
              isLinked
              @select="navYr" />

            <bean-bar
              v-if="!!year && !!yr && mo.length === 0"
              :items="yr"
            />

            <bean-bar
              v-if="!!year && !month" 
              heading="Monthly"
              :items="mos"
              isLinked
              @select="navMo" />


            <bean-bar
              v-if="!!year && !!month && !!mo"
              :items="mo" />

            <bean-bar
              v-if="!year && !month"
              :heading="'Accounts ' + '(' + displayDate + ')'"
              :items="accts"
            />

            <bean-bar
              v-else-if="!!year && !month"
              :heading="'Accounts ' + '(' + displayDate + ')'"
              :items="acctsYrs"
            />

            <bean-bar
              v-else-if="!!year && !!month"
              :heading="'Accounts ' + '(' + displayDate + ')'"
              :items="acctsMos"
            />

            <div v-if="records && records.length">
              <record-list :records="records" :path="path" />
            </div>
          </div>
        </div>
      </div>
    </shell>
  </div>
</template>

<script>
 import { DateTime } from 'luxon';
 import { mapState, mapActions } from 'vuex';
 import eventBus from '@/eventBus';
 import Shell from '@/components/Shell';
 import NoteSelect from '@/components/NoteSelect';
 import RecordList from '@/components/RecordList';
 import BeanBar from '@/components/BeanBar';

 import api from '@/api';

 export default {
   props: {
     path: {
       type: String,
       default: ''
     },

     tags: {
       type: Array,
       default: []
     },

     date: {

     },

     year: {
       type: String
     },

     month: {
       type: String
     },

     mode: {
       type: String,
       default: 'WK'
     }
   },

   components: { Shell, NoteSelect, RecordList, BeanBar },
   data () {
     return {
       totals: [],
       isEnd: false,
       recordOffset: 0,
       isInProgress: false
     }
   },

   computed: {
     ...mapState({
       records: state => state.records
     }),

     routeParams () {
       return {
         year: this.year ? 'yr:' + this.year : undefined,
         month: this.month ? 'mo:' + this.month : undefined
       }
     },

     displayDate () {
       if (!!this.year && !!this.month) {
         return DateTime.fromISO(this.year + '-' + this.month).toFormat('LLLL yyyy');
       } else if (!!this.year && !this.month) {
         return DateTime.fromISO(this.year).toFormat('yyyy');
       } else {
         return 'All time'
       }
     },

     prevPage () {
       if (!!this.year && !!this.month) {
         return {
           name: this.year,
           route: {year: ':yr:' + this.year, month: undefined}
         };
       } else if (!!this.year && !this.month) {
         return {
           name: 'All time',
           route: {
             year: undefined,
             month: undefined
           }
         }
       } else {
         return;
       }
     },

     allTime () {
       return this.totals
                  .filter(t => t.yr === null && t.mo === null && t.acct_id === null)
                  .map(t => {return {...t, label: 'Total'}})
     },

     yrs () {
       return this.totals
                  .filter(t => t.yr !== null && t.mo === null && t.acct_id === null)
                  .sort((a, b) => {
                    return a.yr > b.yr ? -1 : a.yr < b.yr ? 1 : 0
                  })
                  .map(t => {return {...t, label: t.yr}})
     },

     yr () {
       return this.yrs.filter(y => y.yr === this.year).map(y => ({ ...y, label: 'Total ' + y.label }))
     },

     mos () {
       const months = [];

       for (let i=0; i < 12; i++) {
         const mo = ('' + (i + 1)).padStart(2, '0')
         months.push({
           i: 0,
           o: 0,
           net: 0,
           mo,
           yr: this.year,
           label: this.formatDate(this.year, mo)
         });
       }

       this.totals
           .filter(t =>
             t.mo !== null && t.yr === this.year && t.acct_id === null)
           .sort((a, b) => {
             const aSym = a.yr + a.mo;
             const bSym = b.yr + b.mo;
             return aSym > bSym ? -1 : aSym < bSym ? 1 : 0
           }).forEach(t => {
             const i = parseInt(t.mo) - 1;
             Object.assign(months[i], t)
           });

       return months;
     },

     mo () {
       return this.mos
                  .filter(m => (m.yr === this.year) && (m.mo === this.month))
                  .map(m => ({...m, label: 'Total ' + m.label}))
     },


     accts () {
       return this.totals
                  .filter(t => t.acct_id !== null && t.mo === null && t.yr === null)
                  .sort((a, b) => {
                    return a.symbol > b.symbol ? -1 : a.symbol < b.symbol ? 1 : 0
                  })
                  .map(a => ({...a, label: a.symbol}));
     },

     acctsYrs () {
       return this.totals
                  .filter(t => t.acct_id !== null && t.yr === this.year && t.mo === null)
                  .sort((a, b) => {
                    return a.symbol > b.symbol ? -1 : a.symbol < b.symbol ? 1 : 0
                  })
                  .map(a => ({...a, label: a.symbol}));
     },
     acctsMos () {
       return this.totals
                  .filter(t => t.acct_id !== null && t.yr === this.year && t.mo === this.month)
                  .sort((a, b) => {
                    return a.symbol > b.symbol ? -1 : a.symbol < b.symbol ? 1 : 0
                  })
                  .map(a => ({...a, label: a.symbol}));
     },

     start () {
       const d = [this.year, this.month].join('');
       if (d) {
         return DateTime.fromISO(d).toISO();
       }
     },

     end () {
       const dur = {};
       if (this.month) {
         dur.months = 1;
       } else {
         dur.years = 1;
       }

       return DateTime.fromISO(this.start).plus(dur).toISO()
     }
   },

   async created () {
     await this.bootstrap();
     eventBus.$on('record', this.bootstrap);
   },

   destroyed () {
     eventBus.$off('record', this.bootstrap);
   },

   methods: {
     ...mapActions(['getRecords', 'getPaths', 'getTags']),

     async bootstrap() {
       this.getPaths();
       this.getTags();

       this.recordOffset = 0;
       this.isInProgress = true;
       this.isEnd = false;

       const {totals} = await api.getTotals(this.path, this.tags, this.date, this.mode);

       const res = await this.getRecords({
         type: 'tx',
         path: this.path,
         tags: this.tags,
         todo: this.todo,
         start: this.start,
         end: this.end
       });

       if ((res && res.length < 10) || (res && res.err)) {
         this.isEnd = true;
       }

       this.isInProgress = false;
       this.totals = totals;
     },

     async pageItems (done = () => {}) {
       if (this.isInProgress || this.isEnd || this.year || this.month) {
         return done();
       }

       this.isInProgress = true;
       this.recordOffset = this.recordOffset + 1

       const res = await this.getRecords({
         type: 'tx',
         path: this.path,
         tags: this.tags,
         todo: this.todo,
         page: this.recordOffset,
         start: this.start,
         end: this.end
       });

       if ((res && res.length < 10) || (res && res.err)) {
         this.isEnd = true;
       }

       this.$nextTick(() => {
         done();
         this.isInProgress = false;
       });
     },

     goBack(params) {
       const extra =
         (params.year ? params.year : '')
         + (params.month ? '/' + params.month : '')

       this.$nav('beans', {
         extra
       })
     },

     navYr(item) {
       this.$nav('beans', {
         extra: ':yr:' + item.yr
       })
     },

     navMo(item) {
       this.$nav('beans', {
         extra: ':yr:' + item.yr + '/:mo:' + item.mo
       });
     },

     formatDate(year, month) {
       return DateTime.local(parseInt(year), parseInt(month)).toFormat('LLL yyyy');
     }
   },

   watch: {
     $route (to) {
       if (to.name !== 'logout') {
         this.bootstrap();
       }
     }
   }
 };
</script>
